/**
 * ACMLink client helpers — detekce prohlížeče/OS a ping rozšíření.
 * Sdílené Profile + full stránka /core/acm-link.
 */

export type ExtensionStatus = 'checking' | 'found' | 'missing' | 'unsupported';
export type ClientOs = 'windows' | 'linux' | 'macos' | 'unknown';

export interface BrowserInfo {
  name: string;
  chromiumBased: boolean;
  extensionsUrl: string;
  os: ClientOs;
}

export function detectOs(): ClientOs {
  const ua = navigator.userAgent;
  const platform = (navigator.platform || '').toLowerCase();
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const hint = (uaData?.platform || '').toLowerCase();
  if (hint === 'windows' || /win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (hint === 'linux' || /linux/i.test(platform) || (/Linux/i.test(ua) && !/Android/i.test(ua))) {
    return 'linux';
  }
  if (hint === 'macos' || /mac/i.test(platform) || /Mac OS X|Macintosh/i.test(ua)) return 'macos';
  if (/Android/i.test(ua) || /iPhone|iPad/i.test(ua)) return 'unknown';
  return 'unknown';
}

export function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  const nav = navigator as Navigator & { brave?: unknown };
  const os = detectOs();
  if (nav.brave || /\bBrave\//.test(ua)) {
    return { name: 'Brave', chromiumBased: true, extensionsUrl: 'brave://extensions', os };
  }
  if (/Edg\//.test(ua)) {
    return { name: 'Edge', chromiumBased: true, extensionsUrl: 'edge://extensions', os };
  }
  if (/OPR\//.test(ua)) {
    return { name: 'Opera', chromiumBased: true, extensionsUrl: 'opera://extensions', os };
  }
  if (/Vivaldi\//.test(ua)) {
    return { name: 'Vivaldi', chromiumBased: true, extensionsUrl: 'vivaldi://extensions', os };
  }
  if (/Firefox\//.test(ua)) {
    return { name: 'Firefox', chromiumBased: false, extensionsUrl: 'about:addons', os };
  }
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    return { name: 'Safari', chromiumBased: false, extensionsUrl: '', os };
  }
  if (/Chrome\//.test(ua)) {
    return { name: 'Chrome', chromiumBased: true, extensionsUrl: 'chrome://extensions', os };
  }
  return { name: 'unknown', chromiumBased: false, extensionsUrl: '', os };
}

export function pingExtension(timeoutMs = 800): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const handler = (ev: MessageEvent) => {
      if (ev.source !== window) return;
      if (ev.data?.source === 'acm-link-extension' && ev.data?.type === 'pong') {
        done = true;
        window.removeEventListener('message', handler);
        resolve(true);
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ source: 'acm-link-dashboard', type: 'ping' }, '*');
    setTimeout(() => {
      if (!done) {
        window.removeEventListener('message', handler);
        resolve(false);
      }
    }, timeoutMs);
  });
}

/** Origin, přes který user právě vidí dashboard (veřejná doména / IP:port). */
export function currentAcmHost(): string {
  // Nikdy ne internal_ip z API — vždy to, co je v address baru.
  return window.location.origin.replace(/\/+$/, '');
}

export function sendPrefillToExtension(pairingKey: string): void {
  const acmHost = currentAcmHost();
  window.postMessage(
    {
      source: 'acm-link-dashboard',
      type: 'prefill-options',
      acmHost,
      pairingKey,
    },
    '*',
  );
}

export interface CompanionResponse {
  ok: boolean;
  error?: string;
  grant_id?: string;
  proof?: Record<string, unknown>;
  station?: Record<string, unknown>;
  csr_pem?: string;
  local_mcp_url?: string;
}

function companionRequest(
  type: 'agent-access-install' | 'agent-access-revoke' | 'station-proof' | 'station-install' | 'station-csr',
  payload: Record<string, unknown>,
  timeoutMs = 4_000,
): Promise<CompanionResponse> {
  return new Promise((resolve) => {
    const requestId = crypto.randomUUID();
    let settled = false;
    const handler = (ev: MessageEvent) => {
      if (
        ev.source !== window || ev.data?.source !== 'acm-link-extension' ||
        ev.data?.type !== 'agent-access-result' || ev.data?.requestId !== requestId
      ) return;
      settled = true;
      window.removeEventListener('message', handler);
      resolve(ev.data.result as CompanionResponse);
    };
    window.addEventListener('message', handler);
    window.postMessage({
      source: 'acm-link-dashboard',
      type,
      requestId,
      acmHost: currentAcmHost(),
      ...payload,
    }, '*');
    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', handler);
      resolve({ ok: false, error: 'acmlink_companion_unavailable' });
    }, timeoutMs);
  });
}

export function installAgentAccess(bundle: Record<string, unknown>): Promise<CompanionResponse> {
  return companionRequest('agent-access-install', { bundle });
}

export function revokeAgentAccess(grantId: string): Promise<CompanionResponse> {
  return companionRequest('agent-access-revoke', { grantId });
}

export function stationProof(): Promise<CompanionResponse> {
  return companionRequest('station-proof', {}, 12_000);
}
export function installStation(station: Record<string, unknown>): Promise<CompanionResponse> {
  return companionRequest('station-install', { bundle: station }, 15_000);
}

export function stationCSR(): Promise<CompanionResponse> {
  return companionRequest('station-csr', {}, 12_000);
}
